import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Home, Calendar, TrendingUp, Users, Trash2, RefreshCcw, Receipt, Loader2, ShoppingCart } from "lucide-react";
import { clubApi, fmtEur, fmtDate, statusLabel } from "../../lib/clubApi";
import { toast } from "sonner";
import QuickSale from "./QuickSale";

export default function ClubMemberDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            setData(await clubApi.getMember(id));
        } catch {
            toast.error("Cliente non trovato");
            nav("/club/clienti");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const renew = async () => {
        const raw = window.prompt("Quanti mesi rinnovare?", "1");
        if (!raw) return;
        const months = parseInt(raw, 10);
        if (!months || months < 1 || months > 24) return toast.error("Inserisci un numero tra 1 e 24");
        try {
            const r = await clubApi.renewMember(id, { months, method: "contanti" });
            toast.success(`Rinnovato! Nuova scadenza: ${fmtDate(r.membership_end)} · Incassato ${fmtEur(r.total_paid)}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    };

    const del = async () => {
        if (!window.confirm("Eliminare questo cliente e tutta la sua storia? L'operazione è irreversibile.")) return;
        try {
            await clubApi.deleteMember(id);
            toast.success("Cliente eliminato");
            nav("/club/clienti");
        } catch { toast.error("Errore eliminazione"); }
    };

    if (loading || !data) {
        return <div className="grid place-items-center py-16 text-stone-500"><Loader2 className="animate-spin" /></div>;
    }

    const { member: m, sales, payments } = data;
    const st = statusLabel(m.status);

    return (
        <div className="space-y-4" data-testid={`club-detail-${m.id}`}>
            <Link to="/club/clienti" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold">
                <ArrowLeft size={16} /> Clienti
            </Link>

            {/* Header card */}
            <div className="bg-white rounded-3xl p-5 border border-stone-200 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-stone-800">{m.name}</h1>
                        <a href={`tel:${m.phone}`} className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                            <Phone size={14} /> {m.phone}
                        </a>
                        {m.address && (
                            <p className="text-sm text-stone-500 flex items-center gap-1 mt-0.5">
                                <Home size={14} /> {m.address}
                            </p>
                        )}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                </div>
                <div className="text-xs text-stone-500 flex items-center gap-1">
                    <Calendar size={12} /> Tessera fino al <b className="text-stone-700 ml-1">{fmtDate(m.membership_end)}</b>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
                <MiniStat icon={Users} label="Visite" value={m.visits_count} />
                <MiniStat icon={ShoppingCart} label="Scontrino medio" value={fmtEur(m.avg_purchase)} />
                <MiniStat icon={TrendingUp} label="Questo mese" value={fmtEur(m.purchases_this_month)} />
                <MiniStat icon={Receipt} label="Totale" value={fmtEur(m.lifetime_purchases || m.total_purchases)} />
            </div>

            {/* Quick sale of today */}
            <div className="bg-white rounded-3xl p-5 border border-stone-200 shadow-sm" data-testid="club-detail-quick-sale">
                <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                    <ShoppingCart size={18} /> Aggiungi vendita di oggi
                </h3>
                <QuickSale member={m} onSaved={() => load()} />
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
                <button onClick={renew} className="rounded-2xl bg-emerald-500 text-white py-3 font-bold active:bg-emerald-600 inline-flex items-center justify-center gap-2" data-testid="club-renew-btn">
                    <RefreshCcw size={16} /> Rinnova (4,90€)
                </button>
                <button onClick={del} className="rounded-2xl bg-red-50 text-red-600 py-3 font-bold border border-red-200 active:bg-red-100 inline-flex items-center justify-center gap-2" data-testid="club-delete-btn">
                    <Trash2 size={16} /> Elimina
                </button>
            </div>

            {/* Purchase history */}
            <div className="bg-white rounded-3xl p-5 border border-stone-200">
                <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                    <Receipt size={18} /> Vendite ({sales.length})
                </h3>
                {sales.length === 0 ? (
                    <p className="text-sm text-stone-500 text-center py-4">Ancora nessuna vendita registrata.</p>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {sales.map((s) => (
                            <div key={s.id} className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
                                <div>
                                    <p className="font-bold text-stone-800">{fmtEur(s.amount)}</p>
                                    <p className="text-xs text-stone-500">{fmtDate(s.date)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Subscription payments */}
            <div className="bg-white rounded-3xl p-5 border border-stone-200">
                <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                    <Calendar size={18} /> Pagamenti tessera ({payments.length})
                </h3>
                {payments.length === 0 ? (
                    <p className="text-sm text-stone-500 text-center py-4">Nessun pagamento.</p>
                ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {payments.map((p) => (
                            <div key={p.id} className="flex items-center justify-between bg-orange-50 rounded-xl p-3">
                                <div>
                                    <p className="font-bold text-orange-700">{fmtEur(p.amount)}</p>
                                    <p className="text-xs text-stone-500">Periodo {p.period} · {p.method} · {p.note || ""}</p>
                                </div>
                                <span className="text-[10px] text-stone-400">{fmtDate(p.date)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function MiniStat({ icon: Icon, label, value }) {
    return (
        <div className="bg-white rounded-2xl p-3 border border-stone-200">
            <Icon size={16} className="text-orange-500 mb-1" />
            <p className="text-lg font-bold text-stone-800">{value}</p>
            <p className="text-[11px] text-stone-500">{label}</p>
        </div>
    );
}
